import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, GraduationCap, Loader2 } from "lucide-react";
import { ShopLayout, head, money, shopFetch, useCart } from "./shopKit";

const LSO_COPY = {
  created: ["Your LSO training is ready", "Check your email for “Welcome to ALR — set your password”. Set your password, then log in to start the course right away. You have 90 days to finish the course and exam.", "Go to login"],
  unlocked_existing: ["Your LSO training is unlocked", "Log in with your existing ALR account to start the course. You have 90 days to finish the course and exam.", "Log in and start"],
  existing_other_role: ["We'll set up your course access", "This email already has an ALR team login, so our team will contact you within one business day to get you into the course.", null],
  no_email: ["We'll set up your course access", "Our team will contact you within one business day to get you into the course.", null],
};

const LsoNext = ({ grant }) => {
  const c = LSO_COPY[grant];
  if (!c) return null;
  return (
    <div className="mx-auto mt-6 max-w-md rounded-2xl border-2 border-[#4CCC06] bg-[#4CCC06]/10 p-5 text-left" data-testid="shop-thanks-lso">
      <p className="flex items-center gap-2 font-extrabold uppercase" style={head}><GraduationCap size={20} />{c[0]}</p>
      <p className="mt-2 text-sm text-black/70">{c[1]}</p>
      {c[2] && <Link to="/?tab=rental_customer" className="mt-4 inline-block rounded-full bg-black px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#4CCC06] hover:text-black" data-testid="shop-thanks-lso-login">{c[2]}</Link>}
    </div>
  );
};

export default function ShopThanks() {
  const sid = new URLSearchParams(window.location.search).get("session_id");
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const { clear } = useCart();
  const tries = useRef(0);
  useEffect(() => {
    if (!sid) { setErr("We couldn't find your order."); return undefined; }
    let t;
    const poll = async () => {
      try {
        const r = await shopFetch(`/status/${sid}`);
        setO(r);
        if (r.status === "paid" || r.status === "shipped" || r.status === "delivered") { clear(); return; }
      } catch (e) { setErr(e.message); return; }
      if (++tries.current < 15) t = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(t);
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  const paid = o && ["paid", "shipped", "delivered"].includes(o.status);
  return (
    <ShopLayout testId="shop-thanks-page">
      <main className="mx-auto max-w-2xl px-5 pt-16 text-center sm:px-8">
        {err ? <p className="text-lg" data-testid="shop-thanks-error">{err}</p> : !paid ? (
          <div data-testid="shop-thanks-pending">
            <Loader2 className="mx-auto mb-4 animate-spin text-[#4CCC06]" size={40} />
            <p className="text-lg">{tries.current >= 15 ? "Your payment is still processing. You'll get an email as soon as it's confirmed." : "Confirming your payment…"}</p>
          </div>
        ) : (
          <div data-testid="shop-thanks-paid">
            <CheckCircle2 className="mx-auto mb-4 text-[#4CCC06]" size={56} />
            <h1 className="text-4xl font-extrabold uppercase sm:text-5xl" style={head}>Thank you!</h1>
            <p className="mt-3 text-black/60">Order <b data-testid="shop-order-ref">{o.order_ref}</b> is paid ({money(o.total_usd)}). A receipt is on its way to your email{o.ships ? ", and we'll let you know when it ships" : ""}.</p>
            <LsoNext grant={o.lso_grant} />
            <ul className="mx-auto mt-6 max-w-sm text-left text-sm">{o.items.map((i) => <li key={i.name} className="border-b border-black/10 py-2">{i.quantity} × {i.name}</li>)}</ul>
            <Link to="/shop" className="mt-8 inline-block rounded-full bg-black px-6 py-3 font-bold text-white" data-testid="shop-thanks-continue">Keep shopping</Link>
          </div>
        )}
      </main>
    </ShopLayout>
  );
}
